import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useFreeTrialFeature } from '../../contexts/FeatureFlagContext';
import { usePlanPricing, type PlanKey } from '../../lib/planPricing';

interface BillingInfo {
  tenant_id: string;
  plan: string;
  status: string;
  payment_provider?: string;
  stripe_customer_id: string;
  current_period_start: string | null;
  current_period_end: string | null;
  trial_end: string | null;
  usage: {
    current_period_start: string | null;
    current_period_end: string | null;
    usage_by_metric: { [key: string]: number };
    limits_by_metric: { [key: string]: number };
    overages_by_metric: { [key: string]: number };
  };
  recent_invoices: Array<{
    id: string;
    stripe_invoice_id: string;
    status: string;
    amount_due: number;
    amount_paid: number;
    invoice_date: string | null;
    due_date: string | null;
    paid_at: string | null;
    hosted_invoice_url: string | null;
    pdf_url: string | null;
  }>;
}

const BillingPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isFreeTrialEnabled = useFreeTrialFeature();
  const { formattedPlanPrices } = usePlanPricing();
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Helper function to get plan level for comparison
  const getPlanLevel = (plan: string): number => {
    const planLevels: { [key: string]: number } = {
      'starter': 1,
      'growth': 2,
      'agency': 3
    };
    return planLevels[plan.toLowerCase()] || 0;
  };

  // Helper function to determine if it's an upgrade or downgrade
  const getPlanAction = (currentPlan: string, targetPlan: string): string => {
    const currentLevel = getPlanLevel(currentPlan);
    const targetLevel = getPlanLevel(targetPlan);
    return targetLevel > currentLevel ? 'Upgrade' : 'Downgrade';
  };

  useEffect(() => {
    loadBillingInfo();
  }, []);

  // Auto-redirect to checkout if billing is incomplete and card is required for trial
  useEffect(() => {
    // Only check if billing info is loaded
    if (!billingInfo || isLoading) return;
    
    // Don't redirect if we're already redirecting to checkout (prevents race conditions)
    if (sessionStorage.getItem('redirecting_to_checkout') === 'true') {
      console.log('BillingPage: Already redirecting to checkout, skipping auto-redirect');
      return;
    }
    
    // Check if billing is missing (setup_required flag) or incomplete
    const isBillingMissing = (billingInfo as any).setup_required === true;
    const isIncomplete = billingInfo.status === 'incomplete';
    
    // If billing is missing or incomplete, try to redirect to checkout
    if (!isBillingMissing && !isIncomplete) return;
    
    // Check if we've already attempted redirect in this session
    const redirectAttempted = sessionStorage.getItem('incomplete_billing_redirect_attempted') === 'true';
    if (redirectAttempted) return;
    
    // Get plan - use billing info plan, or recover from localStorage, or default to starter
    let planToUse = billingInfo.plan || 'starter';
    if (isBillingMissing) {
      // Try to recover plan from localStorage
      try {
        const pendingRegistration = localStorage.getItem('pending_registration');
        if (pendingRegistration) {
          const registrationData = JSON.parse(pendingRegistration);
          if (registrationData.plan) {
            planToUse = registrationData.plan;
            console.log(`Recovered plan '${planToUse}' from localStorage`);
          }
        }
      } catch (e) {
        console.warn('Failed to recover plan from localStorage:', e);
      }
    }
    
    // Fetch system features to check if card is required
    fetch('/api/v1/system/features', { credentials: 'include' })
      .then(res => {
        if (!res.ok) return null;
        return res.json();
      })
      .then(features => {
        const cardRequired = features?.require_card_for_trial || false;
        if (cardRequired) {
          sessionStorage.setItem('incomplete_billing_redirect_attempted', 'true');
          // Create checkout session with the recovered plan
          api.billing.createCheckoutSession(
            planToUse, // Use recovered plan or plan from billing info
            `${window.location.origin}/billing/checkout-success?session_id={CHECKOUT_SESSION_ID}`,
            `${window.location.origin}/billing`
          ).then((response) => {
            if (response.data.checkout_url) {
              window.location.href = response.data.checkout_url;
            }
          }).catch((error) => {
            console.error('Failed to create checkout session:', error);
            // Clear the flag so user can try again manually
            sessionStorage.removeItem('incomplete_billing_redirect_attempted');
            // Show error but stay on page - user can click button to retry
          });
        }
      })
      .catch(err => {
        console.error('Failed to fetch system features:', err);
        // Don't block the page if feature check fails
      });
  }, [billingInfo, isLoading]);

  // Poll for billing status updates after checkout (in case webhook is delayed)
  useEffect(() => {
    // Only poll if status is incomplete and we're not loading
    if (!billingInfo || isLoading || billingInfo.status !== 'incomplete') return;
    
    // Check if we just came from checkout success
    const fromCheckout = sessionStorage.getItem('from_checkout_success') === 'true';
    if (!fromCheckout) return;
    
    // Clear the flag
    sessionStorage.removeItem('from_checkout_success');
    
    // Poll for status updates (webhook may be delayed)
    let pollCount = 0;
    const maxPolls = 15; // Poll for up to 15 seconds (increased from 10)
    const pollInterval = 1000; // Every 1 second
    
    const pollTimer = setInterval(async () => {
      pollCount++;
      
      try {
        // Invalidate and refetch billing info
        await queryClient.invalidateQueries({ queryKey: ['billing-info'] });
        const response = await api.billing.getInfo();
        const updatedBillingInfo = response.data;
        
        // Update local state
        setBillingInfo(updatedBillingInfo);
        
        // If status is no longer incomplete, stop polling
        if (updatedBillingInfo && updatedBillingInfo.status !== 'incomplete') {
          clearInterval(pollTimer);
          console.log('BillingPage: Status updated to', updatedBillingInfo.status);
        }
      } catch (error) {
        console.error('BillingPage: Error polling billing status:', error);
      }
      
      // Stop polling after max attempts
      if (pollCount >= maxPolls) {
        clearInterval(pollTimer);
        console.log('BillingPage: Polling stopped after max attempts');
      }
    }, pollInterval);
    
    return () => clearInterval(pollTimer);
  }, [billingInfo, isLoading, queryClient]);

  const loadBillingInfo = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.billing.getInfo();
      setBillingInfo(response.data);
    } catch (error: any) {
      console.error('Failed to load billing info:', error);
      if (error.response?.status === 404) {
        setError('Billing not initialized. Please contact support.');
      } else {
        setError(error.response?.data?.detail || 'Failed to load billing information');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpgradePlan = async (plan: string) => {
    try {
      setIsProcessing(true);
      console.log('BillingPage: Creating checkout session for plan:', plan);
      // Use checkout success page for better UX
      const successUrl = `${window.location.origin}/billing/checkout-success?session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${window.location.origin}/billing`;
      
      const response = await api.billing.createCheckoutSession(plan, successUrl, cancelUrl);
      console.log('BillingPage: Checkout response:', response);
      
      // Redirect to payment provider checkout (Stripe or Lemon Squeezy)
      if (response?.data?.checkout_url) {
        console.log('BillingPage: Redirecting to checkout:', response.data.checkout_url);
        window.location.href = response.data.checkout_url;
      } else {
        console.error('BillingPage: No checkout URL in response:', response);
        throw new Error('No checkout URL returned');
      }
    } catch (error: any) {
      console.error('BillingPage: Failed to create checkout session:', error);
      console.error('BillingPage: Error details:', error.response?.data || error.message);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to create checkout session';
      alert(`Error: ${errorMessage}`);
      // Clear redirect flag on error so user can retry
      sessionStorage.removeItem('incomplete_billing_redirect_attempted');
      sessionStorage.removeItem('redirecting_to_checkout');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancelSubscription = async () => {
    const confirmMessage = billingInfo.status === 'trialing' 
      ? 'Are you sure you want to cancel your trial? Your trial will end immediately and you will lose access to all features. You can subscribe again later, but you will not receive another free trial.'
      : 'Are you sure you want to cancel your subscription? Your subscription will end immediately. You can subscribe again later, but you will not receive another free trial.';
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setIsProcessing(true);
      const response = await api.billing.cancelSubscription();
      
      // Show success message
      alert(response.data.message || 'Subscription canceled successfully');
      
      // Invalidate React Query cache first
      queryClient.invalidateQueries({ queryKey: ['billing-info'] });
      
      // Wait a moment for cache to clear, then reload billing info
      await new Promise(resolve => setTimeout(resolve, 500));
      await loadBillingInfo();
      
      // Force another refresh after a short delay to ensure webhook has processed
      setTimeout(async () => {
        await loadBillingInfo();
        queryClient.invalidateQueries({ queryKey: ['billing-info'] });
      }, 2000);
    } catch (error: any) {
      console.error('Failed to cancel subscription:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to cancel subscription. Please try again.';
      alert(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      setIsProcessing(true);
      const returnUrl = `${window.location.origin}/billing`;
      
      const response = await api.billing.createPortalSession(returnUrl);
      
      // Redirect to Stripe Customer Portal
      if (response.data.portal_url) {
        window.location.href = response.data.portal_url;
      }
    } catch (error: any) {
      console.error('Failed to create portal session:', error);
      alert(error.response?.data?.detail || 'Failed to open billing portal');
    } finally {
      setIsProcessing(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const calculatePercentage = (used: number, limit: number) => {
    if (limit === 0) return 0;
    return Math.min((used / limit) * 100, 100);
  };

  const getUsageColor = (used: number, limit: number) => {
    const percentage = calculatePercentage(used, limit);
    if (percentage >= 100) return 'text-red-600 bg-red-100';
    if (percentage >= 80) return 'text-yellow-600 bg-yellow-100';
    return 'text-green-600 bg-green-100';
  };

  const getProgressBarColor = (used: number, limit: number) => {
    const percentage = calculatePercentage(used, limit);
    if (percentage >= 100) return 'bg-red-600';
    if (percentage >= 80) return 'bg-yellow-500';
    return 'bg-blue-600';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading billing information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">⚠️</div>
          <p className="text-gray-800 font-medium">{error}</p>
          <button
            onClick={loadBillingInfo}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!billingInfo) {
    return null;
  }

  const metricLabels: { [key: string]: string } = {
    'email_sends': 'Email Sends',
    'sms_sends': 'SMS Sends',
    'contacts': 'Contacts',
    'feedback_items': 'Feedback Items',
    'storage_gb': 'Storage (GB)'
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Billing & Usage</h1>
          <p className="mt-2 text-gray-600">
            Manage your subscription and view usage details
          </p>
        </div>

        {/* Current Plan */}
        <div className="bg-white shadow rounded-lg p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Current Plan</h2>
              <div className="mt-2">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  {billingInfo.plan.charAt(0).toUpperCase() + billingInfo.plan.slice(1)}
                </span>
                <span className={`ml-2 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  billingInfo.status === 'active' ? 'bg-green-100 text-green-800' :
                  billingInfo.status === 'trialing' ? 'bg-yellow-100 text-yellow-800' :
                  billingInfo.status === 'inactive' ? 'bg-red-100 text-red-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {billingInfo.status.charAt(0).toUpperCase() + billingInfo.status.slice(1)}
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              {/* Show cancel button only for Lemon Squeezy and when status is trialing or active */}
              {(billingInfo.status === 'trialing' || billingInfo.status === 'active') && 
               (billingInfo.payment_provider === 'lemon_squeezy' || !billingInfo.payment_provider) && (
                <button
                  onClick={handleCancelSubscription}
                  disabled={isProcessing}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessing ? 'Processing...' : 'Cancel Subscription'}
                </button>
              )}
              <button
                onClick={handleManageBilling}
                disabled={isProcessing}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? 'Loading...' : 'Manage Billing'}
              </button>
            </div>
          </div>

          {billingInfo.current_period_start && billingInfo.current_period_end && (
            <div className="mt-4 text-sm text-gray-600">
              <p>
                Current billing period: {formatDate(billingInfo.current_period_start)} - {formatDate(billingInfo.current_period_end)}
              </p>
            </div>
          )}

          {/* Canceled/Inactive Status Warning - shows for both inactive and canceled */}
          {billingInfo.status === 'inactive' && (
            <div className="mt-4 p-4 bg-red-50 border-l-4 border-red-500 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-medium text-red-800">
                    Subscription Inactive
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>Your subscription has been canceled or your trial has expired. You can subscribe again to regain access to all features.</p>
                    <div className="mt-3">
                      <button
                        onClick={() => handleUpgradePlan(billingInfo.plan)}
                        disabled={isProcessing}
                        className="bg-red-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isProcessing ? 'Processing...' : 'Subscribe Again'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Trial Expired Warning */}
          {billingInfo.trial_end && new Date(billingInfo.trial_end) <= new Date() && (billingInfo.status === 'inactive' || billingInfo.status === 'trialing') && (
            <div className="mt-4 p-4 bg-red-50 border-l-4 border-red-500 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-medium text-red-800">
                    Your Free Trial Has Expired
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>Your trial ended on {formatDate(billingInfo.trial_end)}. Please upgrade to a paid plan to continue using all features.</p>
                    <div className="mt-3">
                      <button
                        onClick={() => {
                          // Scroll to plans section or trigger upgrade
                          const plansSection = document.getElementById('available-plans');
                          if (plansSection) {
                            plansSection.scrollIntoView({ behavior: 'smooth' });
                          }
                        }}
                        className="bg-red-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        Upgrade Now
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Trial Active Warning */}
          {billingInfo.trial_end && new Date(billingInfo.trial_end) > new Date() && isFreeTrialEnabled && billingInfo.status === 'trialing' && (
            <div className="mt-4 p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    Trial Active
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>Your trial ends on {formatDate(billingInfo.trial_end)}. Upgrade now to avoid any interruption.</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Payment Required (No Trial or Incomplete Status) */}
          {(!isFreeTrialEnabled || ((!billingInfo.trial_end && billingInfo.status === 'incomplete') || (billingInfo as any).setup_required)) && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-red-800">
                    💳 Payment required to start your free trial
                  </p>
                  <p className="text-sm text-red-700 mt-1">
                    Add your payment method to begin your <span className="font-semibold capitalize">{billingInfo.plan}</span> plan trial
              </p>
                </div>
              <button
                  onClick={() => handleUpgradePlan(billingInfo.plan)}
                  disabled={isProcessing}
                  className="ml-4 px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                  {isProcessing ? 'Processing...' : 'Add Payment Method'}
              </button>
              </div>
            </div>
          )}
        </div>

        {/* Usage Metrics */}
        <div className="bg-white shadow rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Usage This Period</h2>
          
          <div className="space-y-6">
            {Object.entries(billingInfo.usage.usage_by_metric).map(([metric, used]) => {
              const limit = billingInfo.usage.limits_by_metric[metric] || 0;
              const overage = billingInfo.usage.overages_by_metric[metric] || 0;
              const percentage = calculatePercentage(used, limit);

              return (
                <div key={metric}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">
                        {metricLabels[metric] || metric}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {used.toLocaleString()} / {limit.toLocaleString()} included
                      </p>
                    </div>
                    <div className="text-right">
                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getUsageColor(used, limit)}`}>
                        {percentage.toFixed(1)}%
                      </span>
                      {overage > 0 && (
                        <p className="text-xs text-red-600 mt-1">
                          Overage: {formatCurrency(overage)}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${getProgressBarColor(used, limit)}`}
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>

          {Object.values(billingInfo.usage.overages_by_metric).some(o => o > 0) && (
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-800">
                💡 <strong>Tip:</strong> Consider upgrading to a higher plan to increase your included limits and reduce overage charges.
              </p>
            </div>
          )}
        </div>

        {/* Available Plans */}
        <div id="available-plans" className="bg-white shadow rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Available Plans</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {['starter', 'growth', 'agency'].map((plan) => (
              <div
                key={plan}
                className={`border-2 rounded-lg p-6 ${
                  billingInfo.plan === plan ? 'border-blue-600 bg-blue-50' : 'border-gray-200'
                }`}
              >
                <h3 className="text-lg font-semibold text-gray-900 capitalize mb-2">
                  {plan}
                </h3>
                <p className="text-3xl font-bold text-gray-900 mb-4">
                  {formattedPlanPrices[plan as PlanKey]}
                  <span className="text-base font-normal text-gray-600">/month</span>
                </p>
                <ul className="space-y-2 mb-6">
                  {plan === 'starter' && (
                    <>
                      <li className="text-sm text-gray-600">✓ 1,000 emails</li>
                      <li className="text-sm text-gray-600">✓ 100 SMS</li>
                      <li className="text-sm text-gray-600">✓ 500 contacts</li>
                    </>
                  )}
                  {plan === 'growth' && (
                    <>
                      <li className="text-sm text-gray-600">✓ 5,000 emails</li>
                      <li className="text-sm text-gray-600">✓ 500 SMS</li>
                      <li className="text-sm text-gray-600">✓ 2,500 contacts</li>
                    </>
                  )}
                  {plan === 'agency' && (
                    <>
                      <li className="text-sm text-gray-600">✓ 20,000 emails</li>
                      <li className="text-sm text-gray-600">✓ 2,000 SMS</li>
                      <li className="text-sm text-gray-600">✓ 10,000 contacts</li>
                    </>
                  )}
                  <li className="text-sm text-gray-600">✓ Pay-as-you-go overages</li>
                </ul>
                {(() => {
                  // Check if this is the current plan AND subscription status
                  const isCurrentPlan = billingInfo.plan === plan;
                  const isTrialExpired = billingInfo.trial_end && new Date(billingInfo.trial_end) <= new Date();
                  const isActive = billingInfo.status === 'active';
                  const isTrialing = billingInfo.status === 'trialing';
                  const isIncomplete = billingInfo.status === 'incomplete';
                  const isInactive = billingInfo.status === 'inactive';
                  const hasActiveTrial = isTrialing && billingInfo.trial_end && new Date(billingInfo.trial_end) > new Date();
                  
                  // If status is inactive, all buttons should say "Upgrade" and be enabled
                  if (isInactive) {
                    return (
                      <button
                        onClick={() => handleUpgradePlan(plan)}
                        disabled={isProcessing}
                        className="w-full px-4 py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed bg-blue-600 text-white hover:bg-blue-700"
                      >
                        {isProcessing ? 'Processing...' : 'Upgrade'}
                      </button>
                    );
                  }
                  
                  // If this is the current plan and it's active or trialing (not incomplete), show status
                  if (isCurrentPlan && (isActive || isTrialing) && !isIncomplete) {
                    let statusText = '';
                    if (isTrialing) {
                      statusText = 'Currently Trialing';
                    } else if (isActive) {
                      statusText = 'Current Plan';
                    }
                    
                    return (
                      <button
                        disabled
                        className="w-full px-4 py-2 bg-gray-300 text-gray-600 rounded-md cursor-not-allowed"
                      >
                        {statusText}
                      </button>
                    );
                  } else if (hasActiveTrial && !isCurrentPlan) {
                    // User is currently trialing on a different plan - disable other plans
                    return (
                      <button
                        disabled
                        className="w-full px-4 py-2 bg-gray-300 text-gray-600 rounded-md cursor-not-allowed"
                      >
                        Available After Trial
                      </button>
                    );
                  } else {
                    // Show appropriate button text for other cases
                    let buttonText = '';
                    if (isProcessing) {
                      buttonText = 'Processing...';
                    } else if (isCurrentPlan && isIncomplete) {
                      buttonText = 'Add Payment Method';
                    } else if (isCurrentPlan && isTrialExpired) {
                      buttonText = 'Upgrade';
                    } else {
                      buttonText = getPlanAction(billingInfo.plan, plan);
                    }
                    
                    return (
                      <button
                        onClick={() => handleUpgradePlan(plan)}
                        disabled={isProcessing}
                        className={`w-full px-4 py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${
                          isCurrentPlan && isIncomplete 
                            ? 'bg-red-600 text-white hover:bg-red-700' 
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        {buttonText}
                      </button>
                    );
                  }
                })()}
              </div>
            ))}
          </div>
        </div>

        {/* Recent Invoices */}
        {billingInfo.recent_invoices && billingInfo.recent_invoices.length > 0 && (
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Recent Invoices</h2>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {billingInfo.recent_invoices.map((invoice) => (
                    <tr key={invoice.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(invoice.invoice_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
                          invoice.status === 'open' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {invoice.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(invoice.amount_due)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {invoice.pdf_url && (
                          <a
                            href={invoice.pdf_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 mr-4"
                          >
                            Download PDF
                          </a>
                        )}
                        {invoice.hosted_invoice_url && (
                          <a
                            href={invoice.hosted_invoice_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800"
                          >
                            View Invoice
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingPage;
